import {Component, HostListener, inject, Inject, Input, LOCALE_ID, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ResponseAvisService} from '../../../../common/services/avis/response-avis.service';
import {SubSink} from '@Common/core/utils/subsink';
import {ActionDataTable, ColumnDataTable, DataTableComponent} from '@Common/widgets/general/data-table';
import {ResponseAvis, ResponseAvisRequest} from '../../../../common/services/avis/response-avis.model';
import {MatDataSourceGeneric} from '@Services/base/daoService';
import {DatePipe} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {ConsultOpinionComponent} from './consult-opinion/consult-opinion.component';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {ResponseForm} from '../../../../common/components/form/models/responsedemat.model';
import {ResponseStateInstruction} from '../../../../common/components/form/models';
import {ResponsesService} from '../../../../common/services/responses/responses.service';
import {ModalHistoricComponent} from "../../../../opinion/historic/modal-historic/modal-historic.component";
import {DocumentService} from "@Services/common/document.service";
import {NewlinePipe, SafeHtmlPipe} from "@Common/widgets/pipes";
import {DomSanitizer} from "@angular/platform-browser";

@Component({
  selector: 'app-list-opinions',
  templateUrl: './list-opinions.component.html',
  styleUrls: ['./list-opinions.component.scss']
})
export class ListOpinionsComponent implements OnInit, OnDestroy {
  @Input() response: ResponseForm;
  @Input() lastInstructionState: ResponseStateInstruction;
  /**
   * Pour utilisation actions gestionnaires ou non (relance, suppression, historique)
   */
  @Input() displayGestionnaire: boolean = false;
  dsAvis: MatDataSourceGeneric<ResponseAvis> = new MatDataSourceGeneric<ResponseAvis>();
  @ViewChild(DataTableComponent) dataTable: DataTableComponent;
  extraParams: Map<string, string> = new Map<string, string>();
  columns: ColumnDataTable[] = [
    {
    columnDef: 'expert', header: 'Sollicité',
    sortField: 'expert_individu__user__last_name',
    display: (row: ResponseAvis) => `${row?.expert_individu_obj?.first_name} ${row?.expert_individu_obj?.last_name}`,
    sort: true, flex: 10
   },
    {
      columnDef: 'dt_created', header: 'Demandé le',
      display: (row: ResponseAvis) => {
        const dp: DatePipe = new DatePipe(this.locale);
        return `<strong>${dp.transform(`${row.dt_updated}`, 'dd/MM/yyyy H:mm')}</strong>`;
      },
      sort: true, flex: 7
    },
    {
      columnDef: 'avis', header: 'Avis',
      display: (row: ResponseAvis) => this._getAvis(row),
      sort: true, flex: 7
    },
    {
      columnDef: 'motivation', header: 'Motivation',
      display: (row: ResponseAvis) => row.motivation,
      tooltip: (row: ResponseAvis) => row.motivation,
      truncate: true,
      sort: false
    },
    {
      columnDef: 'text', header: 'Texte de la demande d\'avis',
      display: (row: ResponseAvis) => this._textDemand(row),
      tooltip: (row: ResponseAvis) => this._textDemand(row),
      truncate: true,
      sort: false
    },
  ];
  actions: ActionDataTable[] = [
    {
      label: 'historic', tooltip: 'Consulter l\'historique de cet avis', icon: 'history',
      click: (row: ResponseAvis) => this._historic(row),
      hidden: (row: ResponseAvis) => row.avis === null || row?.message_question === null || !this.displayGestionnaire
    },
    {
      label: 'remove', tooltip: 'Enlever cette personne pour un avis', icon: 'do_not_disturb_on',
      click: (row: ResponseAvis) => this._removeAvis(row),
      hidden: (row: ResponseAvis) => !!row.avis || !this.displayGestionnaire,
      iconcolor: 'warn'
    },
    {
      label: 'relance', tooltip: 'Relancer ou redemander une demande d\'avis', icon: 'mail',
      click: (row: ResponseAvis) => this._relanceAvis(row),
      hidden: (row: ResponseAvis) => this._isReminderIsHidden(row) || !this.displayGestionnaire
    },
    {
      label: 'consult', tooltip: 'Consulter l\'avis', icon: 'remove_red_eye',
      click: (row: ResponseAvis) => this._consultAvis(row),
      hidden: (row: ResponseAvis) => row.avis === null
    },

  ];
  subSink = new SubSink();
  windowSvc = inject(DocumentService);
  sanitizer = inject(DomSanitizer);

  constructor(@Inject(LOCALE_ID) private locale: string,
              public dialog: MatDialog,
              private snackSvc: SnackBarToastyService,
              private responseSvc: ResponsesService,
              private avisSvc: ResponseAvisService) {
    this.dsAvis.daoService = avisSvc;
  }

  ngOnInit(): void {
    this.extraParams.set('response', this.response.id.toString());
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
  private _getAvis(elt: ResponseAvis) {
    if (! elt.avis && this.responseSvc.isAlreadyManagedState(this.lastInstructionState)) {
      return '<i>La demande a été traitée par un gestionnaire</i>';
    }
    return this.avisSvc.getChipAvis(elt);
  }
  private _textDemand(elt: ResponseAvis, html = false) {
    if (html) {
      const nl: NewlinePipe = new NewlinePipe();
      const sp: SafeHtmlPipe = new SafeHtmlPipe(this.sanitizer);
      return sp.transform(`<p>${nl.transform(elt.message_question)}</p>`);
    }
    return elt.message_question || '';
  }
  /**
   * La relance d'avis n'est possible que si la demande est "En traitement"
   * @param row
   * @private
   */
  private _isReminderIsHidden(row: ResponseAvis) {
    return this.lastInstructionState !== ResponseStateInstruction.in_progress;
  }
  private _removeAvis(avis: ResponseAvis) {
    const data = new DialogData();
    data.title = `Enlever cette personne à donner son avis`;
    data.iconType = 'warning';
    data.message = `<p>${avis.expert_individu_obj.first_name} ${avis.expert_individu_obj.last_name} n'accédera plus à la demande pour donner son avis</p>`;

    const dialogRef = this.dialog.open(DialogValidationComponent, {data: data});
    dialogRef.updatePosition({top: '50px'});
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.subSink.sink = this.avisSvc
          .delete(avis)
          .subscribe(_ => {
            this.snackSvc.toasty('Avis sur cette demande',
            `${avis.expert_individu_obj.first_name} ${avis.expert_individu_obj.last_name} a bien été enlevée, elle ne pourra plus accéder à la demande pour avis`, typeToast.success);
            this.dataTable?.reload();
        });
      }
    });
  }
  private _relanceAvis(avis: ResponseAvis) {
    const data = new DialogData();
    data.title = `Relance d'avis`;
    data.iconType = 'check_circle';
    data.message = `Message d'introduction à cette relance`;
    data.contentData = 'Bonjour, merci de me donner un avis sur cette demande.';
    data.content = true;
    data.contentMinLength = 10;
    data.contentPlaceHolder = `Message d'introduction pour le sollicité`;
    data.buttonTextTrue = 'Relancer';
    data.buttonTextFalse = 'Annuler';
    data.buttonColorTrue = 'primary';

    const dialogRef = this.dialog.open(DialogValidationComponent, { data: data });
    dialogRef.updatePosition({top: '50px'});
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        const _avisRequest = <ResponseAvisRequest> {
          message: result.contentData,
          id: avis.id, response: avis.response,  expert_individu: avis.expert_individu, type_avis: avis.type_avis,
        };
        this.subSink.sink = this.avisSvc
          .requestOrRemindOpinion(_avisRequest)
          .subscribe(_ => this.snackSvc.toasty('Relance de demande d\'Avis', 'la relance a bien été envoyée', typeToast.success));
      }
    });
  }
  private _consultAvis(avis: ResponseAvis) {
    const dialogRef = this.dialog.open(ConsultOpinionComponent, { data: avis });
    dialogRef.updatePosition({top: `50px`});
    dialogRef.updateSize(`700px`);
    dialogRef.afterClosed().subscribe(result => result);
  }
  private _historic(avis: ResponseAvis) {
    const [canvasHeight, canvasWidth] = this.windowSvc.getScreenSize();
    const modalHistoric = this.dialog.open(ModalHistoricComponent, {data: avis});
    modalHistoric.updateSize(`${canvasWidth - 200}px`, `${canvasHeight - 170}px`);
    modalHistoric.updatePosition({top: '50px'});
  }
}
